import { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import { Typography } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import { collection } from '@app/firebase/firestore'
import { formatDateForUI } from '@app/libs/formatters'
import { useCurrentUser } from '@app/context/CurrentUser'

/** Maps member type code to a readable string */
const getMemberTypeLabel = (typeCode) => {
  if (!typeCode || typeCode <= 1) return 'Subscriber'

  const types = {
    2: 'Spouse',
    3: 'Child',
  }

  return types[typeCode] || 'Subscriber'
}

/**
 * Formats an address object into a string
 *
 * @param {Object} address - The address object containing street, city, state, etc.
 * @returns {string} Formatted address string or default placeholder
 */
const formatAddress = (address) => {
  if (!address) return '---'

  return (
    [address.street, address.street2, address.city, address.state, address.zip]
      .filter(Boolean)
      .join(', ')
      .trim() || '---'
  )
}

/**
 * Fetches and formats plan census data
 *
 * @param {string} planID - The ID of the plan to get census data for
 * @returns {Promise<Array>} Formatted member data for the plan
 */
const getPlanCensus = async (planID) => {
  if (!planID) return []

  try {
    // Get all members in one query
    const membersSnapshot = await collection('networks').doc(planID).collection('members').get()

    if (membersSnapshot.empty) return []

    // Process valid members with lastname (more efficient filtering)
    const validMembers = membersSnapshot.docs
      .filter((doc) => doc.data().lastname)
      .map((doc) => ({ ...doc.data(), id: doc.id }))

    if (!validMembers.length) return []

    // Batch fetch profiles more efficiently
    const memberProfiles = await Promise.all(
      validMembers.map(
        (member) =>
          collection('profiles')
            .doc(member.id)
            .get()
            .then((doc) => ({ ...doc.data(), ...member }))
            .catch(() => member), // Fallback to member data if profile fetch fails
      ),
    )

    // Transform data in one pass
    return memberProfiles.map((profile) => ({
      id: profile.memberId || profile.id,
      claimed: profile.claimed ? 'Yes' : 'No',
      firstname: profile.firstname || '---',
      lastname: profile.lastname || '---',
      dob: formatDateForUI(profile.dob) || '---',
      gender: profile.gender || '---',
      address: formatAddress(profile.address),
      phone: profile.phone || '---',
      email: profile.email || '---',
      isDependent: profile.type > 1,
      dependentType: getMemberTypeLabel(profile.type),
    }))
  } catch (error) {
    console.error('Error in getPlanCensus:', error)
    throw error
  }
}

// Column definitions
const columns = [
  { field: 'id', headerName: 'ID', width: 70 },
  { field: 'claimed', headerName: 'Claimed', width: 150 },
  { field: 'firstname', headerName: 'First name', width: 150 },
  { field: 'lastname', headerName: 'Last name', width: 150 },
  { field: 'dob', headerName: 'Date of birth', width: 150 },
  { field: 'gender', headerName: 'Gender', width: 110 },
  { field: 'address', headerName: 'Address', width: 200 },
  { field: 'phone', headerName: 'Phone number', width: 150 },
  { field: 'email', headerName: 'Email', width: 200 },
  { field: 'dependentType', headerName: 'Member type', width: 150 },
]

const MemberCensus = () => {
  const { managedPlans } = useCurrentUser()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!managedPlans?.managedPlanID) {
      setRows([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    getPlanCensus(managedPlans.managedPlanID)
      .then(setRows)
      .catch((err) => {
        console.error('Error getting plan census:', err)
        setError('We encountered an error while fetching the plan census.')
      })
      .finally(() => setLoading(false))
  }, [managedPlans])

  // Custom component for "no rows" state
  const NoRowsOverlay = () => (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
      height='100%'
      p={2}
    >
      <Typography variant='h6' color='text.secondary' gutterBottom>
        No Members Found
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        There are no members enrolled in this plan at this time.
      </Typography>
    </Box>
  )

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
        <CircularProgress size={60} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
        <Typography color='error'>{error}</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <DataGrid
        rows={rows}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 5 },
          },
          filter: {
            filterModel: {
              items: [],
              quickFilterExcludeHiddenColumns: true,
            },
          },
        }}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        checkboxSelection
        slots={{
          toolbar: GridToolbar,
          noRowsOverlay: NoRowsOverlay,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
        sx={{ minHeight: '200px' }}
      />
    </Box>
  )
}

export default MemberCensus
